import React, { useCallback, useState } from 'react';
import classNames from 'classnames';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { generatePath, useNavigate } from 'react-router-dom';
import { displayAlertBoxSagaAction } from 'commons/src/actions';
import TertiaryButton from 'commons/src/components/buttons/TertiaryButton';
import DropdownOptions from 'commons/src/components/dropdown/DropdownOptions';
import MaterialIcon from 'commons/src/components/MaterialIcon';
import { roleRestrictions } from 'commons/src/constants';
import { SensorTypes, ThresholdsResourceScope } from 'commons/src/models/commonEnums';
import { MenuItem } from 'commons/src/models/menuModels';
import { toErrorTypeWithMessage } from 'commons/src/sagas/isErrorType';
import { useUpdateThresholdsMutation } from '../../../api';
import styles from './CustomThresholdSection.module.scss';

export type Props = {
    headerTextKey: string;
    parentLocationId?: string;
    childResourceScope: ThresholdsResourceScope;
    customThresholds: {
        name: string;
        sensors: SensorTypes[];
        id: string;
    }[];
};
export const CustomThresholdSection = ({
    customThresholds,
    headerTextKey,
    parentLocationId,
    childResourceScope,
}: Props): React.ReactElement => {
    const [isExpanded, setIsExpanded] = useState<boolean>(false);
    const { t: txt } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const redirect = (itemId: string): void => {
        const url = parentLocationId
            ? generatePath('/buildings/:locationId/spaces/:spaceId', { locationId: parentLocationId, spaceId: itemId })
            : generatePath('/buildings/:locationId/settings', { locationId: itemId });
        navigate(url);
    };
    const [updateThresholds] = useUpdateThresholdsMutation();

    const removeThresholds = useCallback(
        async (itemId: string): Promise<void> => {
            const item = customThresholds.find(threshold => threshold.id === itemId);
            const sensorList = item?.sensors ?? [];
            const sensorPayload = sensorList.map(sensor => ({
                sensor: sensor as keyof typeof SensorTypes,
                thresholds: [],
            }));

            await updateThresholds({
                id: itemId,
                resourceScope: childResourceScope,
                sensors: sensorPayload,
            })
                .then((): void => {
                    dispatch(displayAlertBoxSagaAction('CustomThresholds.RemoveSuccess', false, true));
                })
                .catch(err => {
                    dispatch(displayAlertBoxSagaAction(toErrorTypeWithMessage(err).message, true, true));
                });
        },
        [childResourceScope, customThresholds]
    );

    const onRemoveThresholds = (itemId: string): void => {
        removeThresholds(itemId).catch();
    };

    return (
        <div className={styles.body}>
            <div className={styles.header}>
                <div className={styles.info}>
                    <MaterialIcon name="info" extraClass={styles.infoIcon} />
                    <div>
                        <Trans
                            i18nKey={txt(headerTextKey, {
                                count: customThresholds.length,
                            })}
                            components={{ bold: <span className={styles.bold} /> }}
                        />
                    </div>
                </div>
                {customThresholds.length > 0 && (
                    <TertiaryButton
                        onClick={(): void => setIsExpanded(!isExpanded)}
                        testAttr="thresholds-items-expand"
                        testId="thresholds-items-expand"
                        icon={<MaterialIcon name={isExpanded ? 'expand_less' : 'expand_more'} />}
                        noColor
                    />
                )}
            </div>
            {isExpanded && (
                <div className={styles.table}>
                    {customThresholds.map(item => {
                        const dropdownOptions: MenuItem[] = [
                            {
                                onClick: () => redirect(item.id),
                                text:
                                    childResourceScope === ThresholdsResourceScope.SPACE
                                        ? txt('CustomThresholds.EditInSpace')
                                        : txt('CustomThresholds.BuildingSettings'),
                                requiredRoleLevel: roleRestrictions.editBuilding,
                                requiredGroupTypes: [],
                            },
                            {
                                onClick: () => onRemoveThresholds(item.id),
                                text: txt('CustomThresholds.RemoveThresholds'),
                                requiredRoleLevel: roleRestrictions.editBuilding,
                                requiredGroupTypes: [],
                            },
                        ];
                        return (
                            <div key={item.id} className={styles.row}>
                                <div className={styles.cellGroup}>
                                    <div className={classNames(styles.cell, styles.overflow)}>{item.name}</div>
                                    <div className={styles.cell}>
                                        {item.sensors.map((sensor, index) => (
                                            <span key={sensor}>
                                                {txt(sensor)}
                                                {index < item.sensors.length - 1 && ', '}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <DropdownOptions
                                        options={dropdownOptions}
                                        id={`option_${item.id}`}
                                        dotMenu
                                        customButton={<MaterialIcon name="more_horiz" />}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default CustomThresholdSection;
